import { Component, DestroyRef, inject, input, OnInit, signal, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { BillingPeriod, BillingPeriodRequest, BillingPeriodService, CustomerAccessStatus, GeneralRegulatoryMode, VatType } from '@eadsysteme/billingclient-v1';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectChange, MatSelectTrigger, MatSuffix } from '@angular/material/select';
import { PeriodService } from '../../period.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateUtil } from '../../../../../util/DateUtil';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialogActions } from '@angular/material/dialog';
import { LayoutService } from '../../../../../../@vex/services/layout.service';
import { SessionService } from '../../../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { DecimalFormatDirective } from '../../../../../directives/decimal-format.directive';
import { fadeInRight400ms } from '../../../../../../@vex/animations/fade-in-right.animation';
import { MatError } from '@angular/material/form-field';

import { NgClass } from '@angular/common';

@Component({
	selector: 'ara-period-settings',
	imports: [
		TranslocoDirective,
		MatFormField,
		MatLabel,
		MatOption,
		MatSelect,
		ReactiveFormsModule,
		MatCheckbox,
		MatTooltip,
		MatButton,
		MatIcon,
		MatDialogActions,
		MatInput,
		MatSuffix,
		DecimalFormatDirective,
		MatError,
		MatSelectTrigger,
		NgClass,
	],
	templateUrl: './period-settings.component.html',
	styleUrl: './period-settings.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInRight400ms],
})
export class PeriodSettingsComponent implements OnInit {
	protected layoutService = inject(LayoutService);
	protected dateUtil = inject(DateUtil);
	private destroyRef = inject(DestroyRef);
	private transloco = inject(TranslocoService);
	private formBuilder = inject(FormBuilder);
	private snackBar = inject(SnackbarService);
	private selectedPeriodService = inject(PeriodService);
	private billingPeriodService = inject(BillingPeriodService);
	private sessionService = inject(SessionService);
	protected readonly CustomerAccessStatus = CustomerAccessStatus;

	billingId = input<string>('');
	periods = input<BillingPeriod[]>([]);
	regulatoryModes = signal<GeneralRegulatoryMode[]>(Object.values(GeneralRegulatoryMode));
	vatTypes = signal<VatType[]>(Object.values(VatType));

	periodFc: FormControl = new FormControl();
	generalRegulatoryModeFc = new FormControl({ value: null, disabled: this.isDisabled });
	vatTypeFc = new FormControl({ value: null, disabled: this.isDisabled });

	germanSettingsFg: FormGroup;

	animationState = signal<'void' | 'visible'>('void');

	ngOnInit(): void {
		if (this.selectedPeriodService.selectedPeriod()) {
			this.periodFc.setValue(this.selectedPeriodService.selectedPeriod());
		} else if (this.periods().length > 0) {
			this.selectedPeriodService.selectedPeriod.set(this.periods()[0]);
			this.periodFc.setValue(this.periods()[0]);
		}

		this.germanSettingsFg = this.formBuilder.group({
			buildingLivingSpace: [{ value: undefined, disabled: this.isDisabled }],
			envelopeNotImprovable: [{ value: false, disabled: this.isDisabled }],
			supplyNotImprovable: [{ value: false, disabled: this.isDisabled }],
			renewableEnergy: [{ value: false, disabled: this.isDisabled }],
			mainlyCommercial: [{ value: false, disabled: this.isDisabled }],
			co2OwnerShare: [{ value: undefined, disabled: this.isDisabled }, [Validators.min(-100), Validators.max(100)]],
			ownerCooperationBilling: [{ value: false, disabled: this.isDisabled }],
			heatGridSupplySince23: [{ value: false, disabled: this.isDisabled }],
			co2SettingsConfirmed: [{ value: false, disabled: this.isDisabled }, Validators.requiredTrue],
		});

		this.loadSettings();
		setTimeout(() => this.animationState.set('visible'), 0);
	}

	loadPeriod($event: MatSelectChange) {
		const selectedPeriod = $event.value;
		this.selectedPeriodService.selectedPeriod.set(selectedPeriod);
		this.animationState.set('void');
		setTimeout(() => {
			this.loadSettings();
			this.animationState.set('visible');
		}, 0);
	}
	get selectedPeriod() {
		return this.selectedPeriodService.selectedPeriod();
	}

	get isDisabled() {
		const status = this.periodFc.value?.customerAccessStatus;
		return status === CustomerAccessStatus.DEACTIVATED || status === CustomerAccessStatus.SUBMITTED;
	}

	private loadSettings() {
		const selectedPeriod = this.selectedPeriodService.selectedPeriod();
		this.vatTypeFc.setValue(selectedPeriod.vatType);
		this.generalRegulatoryModeFc.setValue(selectedPeriod.generalRegulatoryMode);

		if (this.generalRegulatoryModeFc.value === GeneralRegulatoryMode.GERMAN_STANDARD) {
			this.germanSettingsFg.setValue({
				buildingLivingSpace: selectedPeriod.buildingLivingSpace ?? null,
				envelopeNotImprovable: selectedPeriod.envelopeNotImprovable ?? false,
				supplyNotImprovable: selectedPeriod.supplyNotImprovable ?? false,
				renewableEnergy: selectedPeriod.renewableEnergy ?? false,
				mainlyCommercial: selectedPeriod.mainlyCommercial ?? false,
				co2OwnerShare: selectedPeriod.co2OwnerShare ?? null,
				ownerCooperationBilling: selectedPeriod.ownerCooperationBilling ?? false,
				heatGridSupplySince23: selectedPeriod.heatGridSupplySince23 ?? false,
				co2SettingsConfirmed: selectedPeriod.co2SettingsConfirmed ?? false,
			});
		}

		this.generalRegulatoryModeFc.markAsPristine();
		this.vatTypeFc.markAsPristine();
		this.germanSettingsFg.markAsPristine();

		if (this.isDisabled) {
			this.generalRegulatoryModeFc.disable();
			this.vatTypeFc.disable();
			this.germanSettingsFg.disable();
		} else {
			this.generalRegulatoryModeFc.enable();
			this.vatTypeFc.enable();
			this.germanSettingsFg.enable();
		}
	}

	hasChanges(): boolean {
		return !!this.germanSettingsFg.get('co2SettingsConfirmed')?.value;
	}

	save() {
		const current = this.selectedPeriodService.selectedPeriod();
		const periodToSave: BillingPeriodRequest = {
			exportId: current.exportId,
			validFrom: current.validFrom,
			validTo: current.validTo,
			previousBillingPeriodExportId: current.previousBillingPeriodExportId,
			generalRegulatoryMode: this.generalRegulatoryModeFc.value,
			vatType: this.vatTypeFc.value,
			buildingLivingSpace: current.buildingLivingSpace,
			envelopeNotImprovable: current.envelopeNotImprovable,
			supplyNotImprovable: current.supplyNotImprovable,
			renewableEnergy: current.renewableEnergy,
			mainlyCommercial: current.mainlyCommercial,
			co2OwnerShare: current.co2OwnerShare,
			ownerCooperationBilling: current.ownerCooperationBilling,
			heatGridSupplySince23: current.heatGridSupplySince23,
			co2SettingsConfirmed: current.co2SettingsConfirmed,
		};
		if (this.generalRegulatoryModeFc.value === GeneralRegulatoryMode.GERMAN_STANDARD) {
			if (!this.germanSettingsFg.valid) {
				return;
			}
			periodToSave.buildingLivingSpace = this.germanSettingsFg.get('buildingLivingSpace').value;
			periodToSave.co2OwnerShare = this.germanSettingsFg.get('co2OwnerShare').value;

			periodToSave.envelopeNotImprovable = this.germanSettingsFg.get('envelopeNotImprovable').value;
			periodToSave.supplyNotImprovable = this.germanSettingsFg.get('supplyNotImprovable').value;
			periodToSave.renewableEnergy = this.germanSettingsFg.get('renewableEnergy').value;
			periodToSave.mainlyCommercial = this.germanSettingsFg.get('mainlyCommercial').value;
			periodToSave.ownerCooperationBilling = this.germanSettingsFg.get('ownerCooperationBilling').value;
			periodToSave.heatGridSupplySince23 = this.germanSettingsFg.get('heatGridSupplySince23').value;
			periodToSave.co2SettingsConfirmed = this.germanSettingsFg.get('co2SettingsConfirmed').value;
		}

		this.billingPeriodService
			.updateBillingPeriod(this.sessionService.effectiveArea, this.billingId(), periodToSave.exportId, periodToSave)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.snackBar.success(this.transloco.translate('billing.updatedPeriodSuccess'));
					this.loadSettings();
				},
				error: (err) => {
					this.snackBar.error(this.transloco.translate('billing.updatedPeriodError'));
				},
			});
	}

	protected readonly GeneralRegulatoryMode = GeneralRegulatoryMode;
}
